import React, { useEffect, useState } from "react";
import { Monitor, Trash2, Pencil, LogIn, ShieldCheck, ShieldOff, KeyRound, Smartphone, CreditCard, Check, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Section, Btn, Input, cardCls, EmptyState } from "@/components/admin/ui";

const ICONS = {
  signin: LogIn, new_device: Smartphone, device_removed: Trash2, recovery_code: KeyRound,
  mfa_enabled: ShieldCheck, mfa_disabled: ShieldOff, backup_regenerated: KeyRound,
  stripe_connected: CreditCard, stripe_disconnected: CreditCard,
};

export default function Security() {
  const [devices, setDevices] = useState([]);
  const [activity, setActivity] = useState([]);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [hold, setHold] = useState(false);

  const load = () => {
    api.get("/admin/devices").then(({ data }) => setDevices(data)).catch(() => {});
    api.get("/admin/activity").then(({ data }) => setActivity(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => setHold(!!data.require_device_approval)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleHold = async () => {
    const nv = !hold;
    setHold(nv);
    try { await api.put("/admin/settings", { require_device_approval: nv }); toast.success(nv ? "New devices now require approval" : "Device approval requirement turned off"); }
    catch { toast.error("Could not update"); setHold(!nv); }
  };
  const approveDevice = async (key) => { await api.post(`/admin/devices/${key}/approve`); toast.success("Device approved"); load(); };

  const saveName = async (key) => {
    await api.patch(`/admin/devices/${key}`, { name });
    setEditing(null); toast.success("Device renamed"); load();
  };
  const remove = async (key) => {
    if (!window.confirm("Remove this device? A sign-in from it will alert you as new again.")) return;
    await api.delete(`/admin/devices/${key}`); toast.success("Device removed"); load();
  };

  const fmt = (d) => new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid="admin-security">
      <Section title="Security" subtitle="Review sign-ins, manage trusted devices and monitor account activity." />

      <div data-testid="approval-hold-card" className={`${cardCls} p-6 mb-8 flex items-start justify-between gap-4`}>
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} className="text-bd-gold flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-serif text-xl text-bd-ivory">Require approval for new devices</h3>
            <p className="text-xs text-bd-ivory/40 mt-1 max-w-xl">When on, a sign-in from an unrecognised device is held and cannot open the dashboard until you approve it from the alert email (or here). Your current devices are unaffected.</p>
          </div>
        </div>
        <button data-testid="approval-hold-toggle" onClick={toggleHold} role="switch" aria-checked={hold}
          className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${hold ? "bg-bd-gold" : "bg-white/15"}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-bd-black transition-transform ${hold ? "translate-x-6" : ""}`} />
        </button>
      </div>

      <div className={`${cardCls} mb-8`}>
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <Monitor size={18} className="text-bd-gold" /><h2 className="font-serif text-xl text-bd-ivory">Trusted devices</h2>
        </div>
        {devices.length === 0 ? <EmptyState>No devices recorded yet.</EmptyState> : (
          <div className="divide-y divide-white/5">
            {devices.map((d) => (
              <div key={d.key} data-testid={`device-${d.key}`} className="flex items-center gap-4 p-4">
                <Smartphone size={18} className="text-bd-ivory/40 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  {editing === d.key ? (
                    <div className="flex gap-2 max-w-sm">
                      <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                      <button onClick={() => saveName(d.key)} className="text-emerald-400 p-1"><Check size={18} /></button>
                      <button onClick={() => setEditing(null)} className="text-bd-ivory/40 p-1"><X size={18} /></button>
                    </div>
                  ) : (
                    <>
                      <p className="text-bd-ivory truncate flex items-center gap-2">
                        {d.name}
                        {d.status === "pending" && <span className="text-[0.55rem] uppercase tracking-wider border border-bd-gold/50 text-bd-gold px-1.5 py-0.5">Pending</span>}
                        {d.status === "blocked" && <span className="text-[0.55rem] uppercase tracking-wider border border-red-400/50 text-red-400 px-1.5 py-0.5">Blocked</span>}
                      </p>
                      <p className="text-xs text-bd-ivory/40 truncate">{d.ip} · last seen {fmt(d.last_seen)}</p>
                    </>
                  )}
                </div>
                {editing !== d.key && (
                  <>
                    {(d.status === "pending" || d.status === "blocked") && (
                      <button data-testid={`device-approve-${d.key}`} onClick={() => approveDevice(d.key)} title="Approve" className="p-2 text-bd-ivory/40 hover:text-emerald-400"><Check size={16} /></button>
                    )}
                    <button data-testid={`device-rename-${d.key}`} onClick={() => { setEditing(d.key); setName(d.name); }} className="p-2 text-bd-ivory/40 hover:text-bd-gold"><Pencil size={15} /></button>
                    <button data-testid={`device-remove-${d.key}`} onClick={() => remove(d.key)} className="p-2 text-bd-ivory/40 hover:text-red-400"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={cardCls}>
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <ShieldCheck size={18} className="text-bd-gold" /><h2 className="font-serif text-xl text-bd-ivory">Recent activity</h2>
        </div>
        {activity.length === 0 ? <EmptyState>No activity yet.</EmptyState> : (
          <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
            {activity.map((a) => {
              const Icon = ICONS[a.type] || LogIn;
              return (
                <div key={a.id} data-testid={`activity-${a.id}`} className="flex items-center gap-4 p-4">
                  <Icon size={16} className="text-bd-gold/70 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-bd-ivory/90">{a.description}</p>
                    <p className="text-xs text-bd-ivory/35 truncate">{a.ip || "—"}{a.ua ? ` · ${a.ua.slice(0, 60)}` : ""}</p>
                  </div>
                  <span className="text-xs text-bd-ivory/30 whitespace-nowrap">{fmt(a.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
